import { BusinessPersonData } from '@/context/services/tpm/type';
import { isSuccess } from '@/context/services/type';
import { isStrToNumber } from '@/utils/is';
import { QueryFunction } from '@tanstack/react-query';
import type { GetTpmInvoicesParams, GetTpmInvoicesResponse } from 'ap-openapi';
import {
  useGetTpmInvoicesSuspense as useBaseGetTpmInvoicesSuspense,
  useGetTpmInvoices,
  useGetTpmInvoicesHook,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createBusinessPersonData } from './utils';

export const useTpmInvoices = (
  number: string
): GetTpmInvoicesResponse | null => {
  const { data, error } = useGetTpmInvoices(
    {
      number,
    },
    {
      query: {
        enabled: Boolean(number),
      },
    }
  );
  if (error) throw error;
  const body = data?.data;
  if (data && !isSuccess(data.status)) {
    return null;
  }
  return body ?? null;
};

export const useLazyTpmInvoices = () => {
  const [number, setNumber] = useState<string>('');
  const memorizedNumber = useMemo(() => ({ number }), [number]);
  const { error, isLoading, data } = useGetTpmInvoices(memorizedNumber, {
    query: {
      enabled: Boolean(number),
    },
  });

  const [promiseResolver, setPromiseResolver] = useState<{
    resolve: (value: AxiosResponse<GetTpmInvoicesResponse, unknown>) => void;
    reject: (reason?: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (promiseResolver) {
      if (error) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        promiseResolver.reject(error as Error);
        setPromiseResolver(null);
      } else if (!isLoading) {
        promiseResolver.resolve(data!);
        setPromiseResolver(null);
      }
    }
  }, [promiseResolver, error, isLoading, data]);

  const fetch = useCallback(async (number: string) => {
    setNumber(number);
    return new Promise<AxiosResponse<GetTpmInvoicesResponse, unknown>>(
      (resolve, reject) => {
        setPromiseResolver({ resolve, reject });
      }
    );
  }, []);
  return {
    fetch,
    isLoading,
  };
};

export const useGetTpmInvoicesSuspense = (
  number: GetTpmInvoicesParams['number'] | undefined
) => {
  const params = useMemo(() => ({ number: number ?? '' }), [number]);
  const isRequiredParam =
    (number && number.length === 13 && isStrToNumber(number)) || false;
  const getTpmInvoices = useGetTpmInvoicesHook();
  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTpmInvoicesHook>>>
  > = ({ signal }) =>
    // @ts-ignore
    isRequiredParam ? getTpmInvoices(params, {}, signal) : null;
  const { data, error } = useBaseGetTpmInvoicesSuspense(params, {
    query: {
      queryFn: queryFn,
    },
  });
  if (error) throw error;
  return useMemo(() => (data ? data : { status: 404, data: null }), [data]);
};

export const defaultBusinessPersonData: BusinessPersonData = {
  disabled: true,
  parseValidityDate: () => ({ validityStartDate: '', validityEndDate: '' }),
};

export const useDefaultBusinessPersonData = (): BusinessPersonData =>
  useMemo(() => defaultBusinessPersonData, []);

type BusinessPersonDataResponse = {
  status: number;
  data: GetTpmInvoicesResponse | null;
};

export const useConvertTpmResponseToBusinessPersonData = (
  resp: BusinessPersonDataResponse
): BusinessPersonData => {
  const defaultBusinessPersonData = useDefaultBusinessPersonData();
  const setParseValidityDate = useSetParseValidityDate();
  return useMemo(() => {
    if (!isSuccess(resp.status)) {
      return setParseValidityDate(defaultBusinessPersonData);
    }

    const businessPersonBase = createBusinessPersonData(resp.data, false);
    return setParseValidityDate(businessPersonBase);
  }, [defaultBusinessPersonData, resp, setParseValidityDate]);
};

type PartiallyPartial<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export const useSetParseValidityDate = () => {
  const parseValidityDateFn = useParseValidityDate();
  return useCallback(
    (
      data: PartiallyPartial<BusinessPersonData, 'parseValidityDate'>
    ): BusinessPersonData => {
      const parseValidityDate = parseValidityDateFn(data);
      return { ...data, parseValidityDate: parseValidityDate };
    },
    [parseValidityDateFn]
  );
};

const useParseValidityDate = () => {
  return useCallback(
    (data: PartiallyPartial<BusinessPersonData, 'parseValidityDate'>) => {
      if (!data?.term?.length) {
        return () => ({ validityStartDate: '', validityEndDate: '' });
      }

      const registrationPeriod = data.term.find(
        (item) => item.label === '登録'
      );
      if (!registrationPeriod) {
        return () => ({ validityStartDate: '', validityEndDate: '' });
      }

      const [validityStartDate = '', validityEndDate = ''] =
        registrationPeriod.period.toLocaleString().split('〜');
      return () => ({ validityStartDate, validityEndDate });
    },
    []
  );
};
